/**
 * Group detail — events feed + optional AI recommendation (REAL API only).
 * ZD-MOCK: mock package purged — no demo enrichment / demo badge.
 */

import { buildCcEventsPath, buildCcAiIncidentPath } from "../api/dashboard-api.js";

export const GRP_EVENTS_LIMIT = 12;

/**
 * @param {(path: string, o?: object) => Promise<unknown>} api
 * @param {string[]} deviceIds
 */
export async function fetchGroupDetailEvents(api, deviceIds) {
  const ids = (deviceIds || []).map(String).filter(Boolean);
  if (!ids.length) return [];
  const idSet = new Set(ids);
  const res = await api(
    buildCcEventsPath({ query: { limit: String(GRP_EVENTS_LIMIT + 24), order: "desc" } }),
    { timeoutMs: 12000, retries: 0 },
  );
  const items = (res && /** @type {{ items?: unknown[] }} */ (res).items) || [];
  return items
    .filter((ev) => idSet.has(String(/** @type {{ device_id?: string }} */ (ev).device_id || "")))
    .slice(0, GRP_EVENTS_LIMIT);
}

/**
 * @param {unknown[]} events
 * @param {(s: string) => string} escapeHtml
 * @param {(ts: unknown) => string} fmtRel
 */
export function renderGroupActivityListHtml(events, escapeHtml, fmtRel) {
  if (!events.length) {
    return '<p class="muted grp-empty-hint">No recent activity for this group.</p>';
  }
  return events
    .map((raw) => {
      const ev = /** @type {{ id?: number; device_id?: string; level?: string; message?: string; ts?: number }} */ (raw);
      const id = ev.id != null ? String(ev.id) : "";
      const lvl = escapeHtml(String(ev.level || "info"));
      const msg = escapeHtml(String(ev.message || "—").slice(0, 120));
      const when = fmtRel(ev.ts);
      const dev = escapeHtml(String(ev.device_id || "—"));
      const href = id ? `#/incidents/${encodeURIComponent(id)}` : "#/incidents";
      return `<a class="grp-activity-row" href="${href}" style="text-decoration:none;color:inherit">
        <div><span class="badge neutral">${lvl}</span> <span class="mono">${dev}</span></div>
        <div class="muted" style="margin-top:4px">${msg}</div>
        <div class="muted" style="margin-top:4px;font-size:11px">${escapeHtml(when)}</div>
      </a>`;
    })
    .join("");
}

/**
 * @param {HTMLElement|null} el
 * @param {(path: string, o?: object) => Promise<unknown>} api
 * @param {unknown[]} events
 * @param {(s: string) => string} escapeHtml
 */
export async function renderGroupAiRecommendation(el, api, events, escapeHtml) {
  if (!el) return;
  // Only look up AI for real alarm/alert/panic rows — debug/info events have no
  // /ai/incidents/{id} record and would spam 404 in the Network console.
  const pick = (events || []).find((raw) => {
    const lvl = String(/** @type {{ level?: string }} */ (raw).level || "").toLowerCase();
    const msg = String(/** @type {{ message?: string }} */ (raw).message || "").toLowerCase();
    if (msg.startsWith("debug ") || msg === "debug") return false;
    return lvl.includes("alarm") || lvl.includes("alert") || lvl.includes("panic") || lvl.includes("siren");
  });
  if (!pick) {
    el.innerHTML = '<p class="muted grp-empty-hint">No alarm incidents to analyze.</p>';
    return;
  }
  const eventId = /** @type {{ id?: number }} */ (pick).id;
  if (eventId == null) {
    el.innerHTML = '<p class="muted grp-empty-hint">No event id for AI lookup.</p>';
    return;
  }
  el.textContent = "Loading…";
  try {
    const data = await api(buildCcAiIncidentPath(String(eventId)), { timeoutMs: 12000, retries: 0 });
    const enrich = data && /** @type {{ enrichment?: object }} */ (data).enrichment;
    if (!enrich) {
      el.innerHTML = '<p class="muted grp-empty-hint">AI enrichment unavailable.</p>';
      return;
    }
    const e = /** @type {{ risk_score?: number; risk_band?: string; recommended_actions?: unknown[] }} */ (enrich);
    const score = e.risk_score != null ? `${e.risk_score}/100` : "—";
    const band = escapeHtml(String(e.risk_band || "—"));
    const actions = Array.isArray(e.recommended_actions) ? e.recommended_actions : [];
    const actionLine = actions.length
      ? `<p class="muted" style="margin:8px 0 0">${escapeHtml(String(actions[0]))}</p>`
      : "";
    el.innerHTML = `<p style="margin:0"><strong>${band}</strong> · ${escapeHtml(score)}</p>${actionLine}`;
  } catch (err) {
    const msg = String((err && err.message) || err || "").toLowerCase();
    const soft404 = msg.includes("404") || msg.includes("not found") || msg.includes("ai_incident");
    el.innerHTML = soft404
      ? '<p class="muted grp-empty-hint">No AI analysis for this event yet.</p>'
      : '<p class="muted grp-empty-hint">AI lookup failed.</p>';
  }
}
